import React, { useState, useMemo } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useSwipeable } from 'react-swipeable'
import NavBar from '../../components/NavBar/NavBar.jsx'
import { useAuthStore } from '../../stores/authStore'
import { useTenantStore } from '../../stores/tenantStore'
import { MarketplaceSidebar } from '../../features/marketplace/components/MarketplaceSidebar/MarketplaceSidebar'
import { AdminSidebar } from '../../features/admin/shared/components/AdminSidebar/AdminSidebar'
import LanesSidebar from '../../features/lanes/components/LanesSidebar/LanesSidebar.jsx'
import Sidebar from '../../features/orders/components/OrderEditor/components/Sidebar/Sidebar.jsx'
import { isDrayNow } from '../../lib/config/multitenancy.js'
import {
  ViewRoot,
  ContentContainer,
  Sidebar as SidebarContainer,
  ContentViewport,
  Content,
} from '../../components/View/View.styled.js'

export function AppLayout() {
  const { pathname } = useLocation()
  const [isSidebarVisible, setIsSidebarVisible] = useState(true)

  const toggleSidebar = () => setIsSidebarVisible((prev) => !prev)
  const swipeable = useSwipeable({
    onSwipedLeft: () => setIsSidebarVisible(false),
    onSwipedRight: () => setIsSidebarVisible(true),
  })

  const sidebar = useSidebar(pathname)
  const needsSupportButtonPadding = isDrayNow

  return (
    <ViewRoot>
      <NavBar
        isSidebarShown={isSidebarVisible}
        onToggleSidebar={toggleSidebar}
      />
      <ContentContainer>
        {sidebar && (
          <SidebarContainer
            hidden={!isSidebarVisible}
            $extraBottomPadding={needsSupportButtonPadding}
            {...swipeable}
          >
            {sidebar}
          </SidebarContainer>
        )}
        <ContentViewport>
          <Content>
            <Outlet />
          </Content>
        </ContentViewport>
      </ContentContainer>
    </ViewRoot>
  )
}

function useSidebar(pathname) {
  const { user } = useAuthStore()
  const isFeatureEnabled = useTenantStore((s) => s.isFeatureEnabled)
  const features = useTenantStore((s) => s.features)

  const tenant = useMemo(
    () => ({ isFeatureEnabled, features }),
    [isFeatureEnabled, features],
  )
  const userRole = useMemo(() => user?.role || 'user', [user])

  return useMemo(() => {
    if (
      pathname.startsWith('/marketplace') ||
      pathname.startsWith('/streetturns') ||
      pathname.startsWith('/containers')
    )
      return <MarketplaceSidebar tenant={tenant} userRole={userRole} />
    if (pathname.startsWith('/admin')) return <AdminSidebar />
    if (pathname.startsWith('/lanes')) return <LanesSidebar />
    if (pathname.startsWith('/order')) return <Sidebar isLane={false} />
    return null
  }, [pathname, tenant, userRole])
}
